import React, { useState } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import { formatCurrency } from '../../../utils/formatters';
import {
  createIRSFormOtherItem,
  updateIRSFormOtherItem,
  deleteIRSFormOtherItem,
} from '../../../endpoints/irsForms';
import { IRSOtherLineItem } from '../../../types/irsForms';

interface IRSFormOtherCategoryProps {
  formId: string;
  questionId: string;
  items: IRSOtherLineItem[];
  editable: boolean;
  onUpdate: () => void;
}

const IRSFormOtherCategory: React.FC<IRSFormOtherCategoryProps> = ({
  formId,
  questionId,
  items,
  editable,
  onUpdate,
}) => {
  const [newDescription, setNewDescription] = useState('');
  const [newAmount, setNewAmount] = useState<number | ''>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editAmount, setEditAmount] = useState<number | ''>('');

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreateItem = async (data: {
    description: string;
    amount: number;
  }) => {
    setIsCreating(true);
    try {
      await createIRSFormOtherItem(formId, questionId, data);
      setNewDescription('');
      setNewAmount('');
      onUpdate();
    } catch (error) {
      console.error('Failed to create line item:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateItem = async (data: {
    itemId: string;
    description: string;
    amount: number;
  }) => {
    setIsUpdating(true);
    try {
      await updateIRSFormOtherItem(formId, questionId, data.itemId, {
        description: data.description,
        amount: data.amount,
      });
      setEditingId(null);
      setEditDescription('');
      setEditAmount('');
      onUpdate();
    } catch (error) {
      console.error('Failed to update line item:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    setIsDeleting(true);
    try {
      await deleteIRSFormOtherItem(formId, questionId, itemId);
      onUpdate();
    } catch (error) {
      console.error('Failed to delete line item:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAdd = () => {
    if (newDescription.trim() && newAmount !== '') {
      handleCreateItem({
        description: newDescription.trim(),
        amount: typeof newAmount === 'number' ? newAmount : 0,
      });
    }
  };

  const handleEdit = (item: IRSOtherLineItem) => {
    setEditingId(item.id);
    setEditDescription(item.description || '');
    setEditAmount(item.amount || '');
  };

  const handleSave = () => {
    if (editingId && editDescription.trim() && editAmount !== '') {
      handleUpdateItem({
        itemId: editingId,
        description: editDescription.trim(),
        amount: typeof editAmount === 'number' ? editAmount : 0,
      });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditDescription('');
    setEditAmount('');
  };

  const handleDelete = (itemId: string) => {
    if (window.confirm('Are you sure you want to delete this line item?')) {
      handleDeleteItem(itemId);
    }
  };

  const total = items.reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <Grid container spacing={2}>
      {/* Items table */}
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                {editable && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={editable ? 3 : 2} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No line items added yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id}>
                    {editingId === item.id ? (
                      <>
                        <TableCell>
                          <TextField
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            fullWidth
                            size="small"
                            disabled={isUpdating}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            value={editAmount}
                            onChange={(e) =>
                              setEditAmount(
                                e.target.value ? parseFloat(e.target.value) : ''
                              )
                            }
                            size="small"
                            disabled={isUpdating}
                            InputProps={{
                              startAdornment: (
                                <Typography sx={{ mr: 1 }}>$</Typography>
                              ),
                            }}
                            sx={{ width: 150 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="primary"
                            onClick={handleSave}
                            disabled={
                              isUpdating ||
                              !editDescription.trim() ||
                              editAmount === ''
                            }
                            size="small"
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton
                            onClick={handleCancel}
                            disabled={isUpdating}
                            size="small"
                          >
                            <CancelIcon />
                          </IconButton>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{item.description || ''}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.amount || 0)}
                        </TableCell>
                        {editable && (
                          <TableCell align="right">
                            <IconButton
                              color="primary"
                              onClick={() => handleEdit(item)}
                              disabled={isDeleting || editingId !== null}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(item.id)}
                              disabled={isDeleting || editingId !== null}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        )}
                      </>
                    )}
                  </TableRow>
                ))
              )}
              {items.length > 0 && (
                <TableRow>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">
                      Total
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(total)}
                    </Typography>
                  </TableCell>
                  {editable && <TableCell />}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* Add new item form */}
      {editable && (
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  label="Description"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={isCreating}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Amount"
                  type="number"
                  value={newAmount}
                  onChange={(e) =>
                    setNewAmount(
                      e.target.value ? parseFloat(e.target.value) : ''
                    )
                  }
                  fullWidth
                  size="small"
                  disabled={isCreating}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  disabled={
                    isCreating || !newDescription.trim() || newAmount === ''
                  }
                  fullWidth
                >
                  Add Item
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default IRSFormOtherCategory;
